/**
 * E-Prescription Organization Section
 *
 * Section for organization (clinic) e-prescription connection status
 * and setup. Displayed in Practice Settings page.
 *
 * Styled as a purple-bordered card with a step-based layout:
 *  - Header: "E-Prescribing (JotRx)" with NEW badge
 *  - Step 1: Connect to DoseSpot
 *  - Contextual prompts for practice_connected / registered states
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowForward, CheckCircle, Medication, Shield } from '@mui/icons-material';
import { alpha, Avatar, Box, Button, Card, Chip, CircularProgress, Typography } from '@mui/material';

import { useJotRxSetupState } from '../hooks/useJotRxSetupState';
import { useEPrescriptionStatus } from '../store';

import { EPrescriptionOrgSetupModal } from './EPrescriptionOrgSetupModal';

export function EPrescriptionOrgSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { isOrgConnected, isLoading } = useEPrescriptionStatus();
  const { setupState } = useJotRxSetupState();

  if (isLoading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: 2,
          borderColor: theme => alpha(theme.palette.primary.main, 0.2),
        }}
      >
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Loading e-prescription settings...</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <>
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: 2,
          borderColor: theme => alpha(theme.palette.primary.main, 0.2),
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: theme => alpha(theme.palette.primary.main, 0.1),
            bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Medication sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              E-Prescribing (JotRx)
            </Typography>
            <Chip
              label="NEW"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.625rem',
                fontWeight: 700,
                bgcolor: theme => alpha(theme.palette.warning.main, 0.1),
                color: 'warning.dark',
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Write and manage prescriptions directly from JotPsych via DoseSpot
          </Typography>
        </Box>

        {/* Step 1: DoseSpot Connection */}
        <Box sx={{ px: 3, py: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: isOrgConnected
                  ? theme => alpha(theme.palette.success.main, 0.1)
                  : 'grey.100',
                flexShrink: 0,
              }}
            >
              {isOrgConnected ? (
                <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
              ) : (
                <Shield sx={{ fontSize: 20, color: 'text.secondary' }} />
              )}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Step 1: Connect to DoseSpot
                </Typography>
                {isOrgConnected && (
                  <Chip
                    label="Connected"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.625rem',
                      fontWeight: 600,
                      bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                      color: 'success.main',
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                {isOrgConnected
                  ? 'Your practice is connected to DoseSpot. Prescriptions will be routed through your DoseSpot account.'
                  : 'Connect your practice to DoseSpot to enable e-prescribing for your clinicians.'}
              </Typography>
              {!isOrgConnected && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIsModalOpen(true)}
                  endIcon={<ArrowForward sx={{ fontSize: '14px !important' }} />}
                  sx={{
                    mt: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    borderRadius: 1.5,
                    px: 2,
                    py: 0.75,
                    boxShadow: 1,
                  }}
                >
                  Connect to DoseSpot
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* practice_connected: prompt to register in Account Settings */}
        {setupState === 'practice_connected' && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 2,
                borderRadius: 2,
                border: 2,
                borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <CheckCircle sx={{ fontSize: 20, color: 'success.main', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Clinic connected successfully
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Next, go to Account Settings to register as a prescriber with DoseSpot.
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                endIcon={<ArrowForward sx={{ fontSize: '14px !important' }} />}
                onClick={() => navigate('/user')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  borderRadius: 1.5,
                  px: 2,
                  py: 0.75,
                  boxShadow: 1,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                Next: Register
              </Button>
            </Box>
          </Box>
        )}

        {/* registered: prompt to complete identity proofing */}
        {setupState === 'registered' && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 2,
                borderRadius: 2,
                border: 2,
                borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <CheckCircle sx={{ fontSize: 20, color: 'success.main', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Almost done
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Complete identity proofing in DoseSpot to start prescribing.
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                endIcon={<ArrowForward sx={{ fontSize: '14px !important' }} />}
                onClick={() => navigate('/user')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  borderRadius: 1.5,
                  px: 2,
                  py: 0.75,
                  boxShadow: 1,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                Go to Account Settings
              </Button>
            </Box>
          </Box>
        )}

        {/* idp_complete: IDP done, TFA needed */}
        {setupState === 'idp_complete' && (
          <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
            {/* Step 2: Identity Proofing — complete */}
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                    flexShrink: 0,
                  }}
                >
                  <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Step 2: Identity Proofing
                    </Typography>
                    <Chip
                      label="Complete"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                        color: 'success.main',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    Your identity has been verified by DoseSpot.
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Step 3: TFA — pending */}
            <Box sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: 'grey.100',
                    flexShrink: 0,
                  }}
                >
                  <Shield sx={{ fontSize: 20, color: 'text.secondary' }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Step 3: Two-Factor Authentication
                    </Typography>
                    <Chip
                      label="Action Required"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        bgcolor: theme => alpha(theme.palette.warning.main, 0.1),
                        color: 'warning.dark',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    Set up TFA in DoseSpot to enable controlled substance prescribing.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<ArrowForward sx={{ fontSize: '14px !important' }} />}
                    onClick={() => navigate('/user')}
                    sx={{
                      mt: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      borderRadius: 1.5,
                      px: 2,
                      py: 0.75,
                      boxShadow: 1,
                    }}
                  >
                    Go to Account Settings
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* fully_setup: ready to prescribe */}
        {setupState === 'fully_setup' && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                  flexShrink: 0,
                }}
              >
                <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  You&apos;re ready to prescribe from JotPsych
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Both your practice and clinician accounts are connected. You can write
                  prescriptions directly from the recorder page.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Card>

      <EPrescriptionOrgSetupModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
